import API from './api';
import { COMMUNUCATION_CHANNEL } from '../../redux/actions/rootAction';
import { resolveRecipientName } from '../../components/helpers/userFieldsData';

import axios from 'axios';

const formatApiErrorBody = body => {
  if (!body || typeof body !== 'object') {
    return '';
  }
  return [body.errorCode, body.errorMessage || body.error, body.infoMessage]
    .filter(Boolean)
    .join(': ');
};

export const getResponseErrorMessage = error => {
  if (typeof error === 'string') {
    return error;
  }

  const fromApiBody =
    formatApiErrorBody(error?.response?.data) ||
    formatApiErrorBody(error?.response) ||
    formatApiErrorBody(error);

  const logData = {
    message: error?.message,
    name: error?.name,
    code: error?.code,
    stack: error?.stack,

    responseStatus: error?.response?.status,
    responseData: error?.response?.data,
    fromApiBody: fromApiBody || undefined,

    requestUrl: error?.config?.url,
    requestMethod: error?.config?.method,
    visibility: document.visibilityState,
    online: navigator.onLine
  };
  return JSON.stringify(logData);
};

export default class PaperflyAPI {
  static listRequestedDocuments = (
    recipientRequestId,
    pinOrOtpRequestId,
    sessionId,
    deviceId
  ) =>
    API.post('requesteddocument/list', {
      recipientRequestId,
      ...(sessionId && { sessionId: sessionId, deviceId: deviceId }),
      ...(pinOrOtpRequestId?.otpRequestId
        ? {
            otpRequestId: pinOrOtpRequestId.otpRequestId,
            ...(pinOrOtpRequestId.pin && { pin: pinOrOtpRequestId.pin })
          }
        : { ...(pinOrOtpRequestId && { pin: pinOrOtpRequestId }) })
    });

  static listRequestedDocumentsTemplate = (templateId, sessionId, deviceId) =>
    API.post('requesteddocument/listfortemplate', {
      templateId,
      ...(sessionId && { sessionId, deviceId })
    });

  static createRequestFromTemplate = (
    templateId,
    usersData,
    testMode,
    sessionId,
    deviceId
  ) => {
    const prepareRecipient = data => {
      const useEmail = data.userEmail.indexOf('@') > -1;
      return {
        id: data.id,
        communicationChannel: useEmail
          ? COMMUNUCATION_CHANNEL.EMAIL
          : COMMUNUCATION_CHANNEL.PHONE,
        recipientPhone: data.userPhone || (useEmail ? null : data.userEmail),
        recipientEmail: useEmail ? data.userEmail : null,
        recipientName: resolveRecipientName(data),
        qesFirstName: data.qesFirstName,
        qesLastName: data.qesLastName
      };
    };

    return API.post('paperflycase/createFromTemplate', {
      templateId: templateId,
      recipients: usersData
        .filter(u => u.enabled)
        .map(data => prepareRecipient(data)),
      embeddingFields: usersData[0].userFields,
      testMode,
      ...(sessionId && { sessionId, deviceId })
    });
  };

  static uploadRequestedDocumentPage = (
    recipientRequestId,
    documentId,
    pageId,
    data,
    sourcePdf,
    lastPage
  ) =>
    API.post('requesteddocument/upload', {
      recipientRequestId,
      documentId,
      pageId,
      data,
      sourcePdf,
      lastPage
    });

  static uploadIdCheck = (
    recipientRequestId,
    documentId,
    docFrontData,
    docBackData,
    selfieData,
    idCheckDocType
  ) =>
    API.post('requesteddocument/upload-id-check', {
      recipientRequestId,
      documentId,
      docFrontData,
      docBackData,
      selfieData,
      idCheckDocType
    });

  static submitRequestedDocumentForm = (
    recipientRequestId,
    documentId,
    formData,
    signatureTouchesId,
    signatureTouches
  ) => {
    return API.post('requesteddocument/submit', {
      recipientRequestId,
      documentId,
      formData,
      ...(signatureTouches && {
        biometricData: {
          [signatureTouchesId]: signatureTouches
        }
      })
    });
  };

  static getDocumentState = (recipientRequestId, documentId) => {
    return API.post('requesteddocument/get-state', {
      recipientRequestId,
      documentId
    });
  };

  /**
   *
   * @param recipientRequestId
   * @param documentId
   * @param file
   * @returns {Promise<{success: boolean, errorMessage: string, responseError: Object}>}
   */
  static submitRequestedDocumentPdf = (
    recipientRequestId,
    documentId,
    file
  ) => {
    return new Promise((resolve, reject) => {
      if (!file || file.size === 0) {
        reject({
          success: false,
          errorMessage: 'Cant upload PDF, no file or it size 0',
          responseError: ''
        });
        return;
      }
      API.post('requesteddocument/signUrl', {
        recipientRequestId: recipientRequestId,
        documentId: documentId,
        sourcePdfName: file.name
      }).then(
        response => {
          let options = {
            headers: {
              'Content-Type': file.type,
              'Cache-Control': 'public, max-age=31536000, immutable',
              'x-amz-acl': 'bucket-owner-full-control'
            },
            crossDomain: true
          };
          if (!response.signedUrl) {
            reject({
              success: false,
              errorMessage: 'Error on upload PDF, cant obtain signedURL',
              responseError: response
            });
          }
          axios.put(response.signedUrl, file, options).then(
            answer => {
              if (answer.statusText && answer.statusText !== 'OK') {
                reject({
                  success: false,
                  errorMessage:
                    'Error on upload PDF by signedURL, status is not OK',
                  responseError: answer
                });
              } else {
                resolve({
                  success: true
                });
              }
            },
            error => {
              reject({
                success: false,
                errorMessage: 'Error on upload PDF by signedURL',
                responseError: error
              });
            }
          );
        },
        error => {
          reject({
            success: false,
            errorMessage: 'Error on create signedURL for PDF',
            responseError: error
          });
        }
      );
    });
  };

  static submitErrorProtocol = (recipientRequestId, errKey, protocol = '') => {
    // S3 error tracing is collected only for production stage.
    if (process.env.REACT_APP_ENV !== 'production') {
      return;
    }
    window.pwaSessionWithErrors = true;
    let options = {
      headers: {
        'Content-Type': 'text/plain',
        'Cache-Control': 'public, max-age=31536000, immutable',
        'x-amz-acl': 'bucket-owner-full-control'
      },
      crossDomain: true
    };
    axios
      .put(
        `https://pwa-error-tracing.s3.eu-central-1.amazonaws.com/${new Date()
          .toISOString()
          .slice(0, 10)}/${process.env.REACT_APP_ENV}/${
          window.pwaSession
        }-${recipientRequestId}/${errKey}_${new Date().toISOString()}.txt`,
        window.globalLog + '\r\n' + protocol,
        options
      )
      .then(
        answer => {
          console.log(answer);
        },
        error => {
          console.log(error);
        }
      );
  };

  static acceptRequestedDocumentForInformation = (
    recipientRequestId,
    documentId
  ) => {
    return API.post('requesteddocument/accept', {
      recipientRequestId,
      documentId
    });
  };

  static createAndGetDemoRequestId = recipientRequestId => {
    return API.post('demo/create', { recipientRequestId });
  };

  static completeRequest = recipientRequestId => {
    return API.post('paperflyrequest/complete', {
      recipientRequestId
    });
  };

  static completeStep = (recipientRequestId, pageId) => {
    return API.post('paperflyrequest/completeStep', {
      recipientRequestId,
      pageId,
      pageState: 'COMPLETED'
    });
  };

  static registerEmail = (recipientRequestId, recipientEmail) => {
    return API.post('backchannel/register', {
      recipientRequestId,
      recipientEmail
    });
  };

  static merkurRegisterEmail = (
    recipientRequestId,
    recipientEmail,
    recipientPhone
  ) => {
    return API.post('paperflyrequest/updatechannel', {
      recipientRequestId,
      recipientEmail: recipientEmail,
      recipientPhone: recipientPhone
    });
  };

  static tempDataUploadPage = (recipientRequestId, pageId, data) => {
    return API.post('intermediarypaperflyrequest/upload', {
      recipientRequestId,
      pageId,
      data
    });
  };

  static tempDataPush = (recipientRequestId, requestedDocuments) => {
    return API.post('intermediarypaperflyrequest/put', {
      recipientRequestId,
      requestedDocuments
    });
  };

  static tempDataPull = recipientRequestId => {
    return API.post('intermediarypaperflyrequest/pull', {
      recipientRequestId
    });
  };

  static signmeCreateRegistrationProcess = (
    mobilePhoneNumber,
    emailAddress,
    givenNames,
    familyNames,
    recipientRequestId,
    rememberUser,
    waiter
  ) =>
    API.post('signme/pwaCreateRegistrationProcess', {
      mobilePhoneNumber,
      emailAddress,
      givenNames,
      familyNames,
      rememberUser,
      recipientRequestId,
      waiter
    });

  static signmeCreateSignatureProcess = (
    recipientRequestId,
    documentIds,
    signmeIdentityId
  ) =>
    API.post('signme/pwaCreateSignatureProcess', {
      documentIds,
      recipientRequestId,
      signmeIdentityId
    });

  static signmeGetUser = (
    recipientRequestId,
    emailAddress,
    mobilePhoneNumber,
    familyNames,
    givenNames
  ) =>
    API.post('signme/pwaGetUser', {
      recipientRequestId,
      emailAddress: emailAddress ? emailAddress.toLowerCase() : undefined,
      mobilePhoneNumber,
      familyNames: familyNames ? familyNames.trim() : undefined,
      givenNames: givenNames ? givenNames.trim() : undefined
    });

  static getSignmeSignatureStatus = internalCallbackId =>
    API.post('signme/pwaGetsignatureprocess ', {
      internalCallbackId
    });

  static getYesSignatureStatus = internalCallbackId =>
    API.post('yes/getsignatureprocess', {
      internalCallbackId
    });

  static getAtrustRegisterStatus = recipientRequestId =>
    API.post('atrust/registration/get', {
      recipientRequestId
    });

  static getAtrustWebIdStart = (recipientRequestId, registerData) =>
    API.post('atrust/web-id/start', {
      recipientRequestId,
      ...registerData
    });

  static getAtrustSignatureStatus = internalCallbackId =>
    API.post('atrust/pwagetsignatureprocess', {
      internalCallbackId
    });

  static yesCreateSignatureProcess = (recipientRequestId, signingDocuments) =>
    API.post('yes/createsignatureprocess', {
      recipientRequestId,
      signingDocuments
    });

  static atrustCreateSignatureProcess = (
    recipientRequestId,
    signingDocuments
  ) =>
    API.post('atrust/pwasigndocuments', {
      recipientRequestId,
      signingDocuments
    });

  static signOnMobile = (documentName, widgetTitle) => {
    return API.post('mobilesignature/create', {
      documentName,
      widgetTitle
    });
  };

  static getSignOnMobileStatus = mobileSignatureId => {
    return API.post('mobilesignature/get', {
      mobileSignatureId
    });
  };

  static setSignedOnMobile = (mobileSignatureId, signature, biometricData) => {
    return API.post('mobilesignature/update', {
      mobileSignatureId,
      signature,
      biometricData
    });
  };

  static finalisePdfDoc = (id, recipientRequestId, recipientIds, formData) => {
    return API.post('requesteddocument/pwaapplydata', {
      tempMode: false,
      recipientRequestId,
      documents: [
        {
          id,
          recipientIds,
          formData
        }
      ]
    });
  };

  static otpRequestVerification = recipientRequestId => {
    return API.post('otp/pwasend', {
      recipientRequestId
    });
  };

  static otpVerifyCode = (otpRequestId, otp) => {
    return API.post('otp/verify', {
      otpRequestId,
      otp
    });
  };

  static getDownloadRequiredUrl = (recipientRequestId, documentId) => {
    return API.post('requesteddocument/download', {
      recipientRequestId,
      documentId
    });
  };

  static getDownloadLink = (recipientRequestId, otpRequestId, pin) => {
    return API.post('download-url/get', {
      recipientRequestId,
      otpRequestId,
      pin
    });
  };

  static getPdfDownloadLink = (recipientRequestId, documentIds) => {
    return API.post(
      'pdf/pwa-create',
      {
        recipientRequestId,
        documentIds
      },
      0
    );
  };

  static rejectDocument = (
    recipientRequestId,
    documentId,
    reason,
    formData
  ) => {
    return API.post('requesteddocument/reject', {
      recipientRequestId,
      documentId,
      reason,
      formData
    });
  };

  static crossDeviceDocumentCreate = (
    recipientRequestId,
    documentId,
    embedded,
    documentOptions
  ) => {
    return API.post('crossdevicedocument/create', {
      recipientRequestId,
      documentId,
      embedded,
      documentOptions
    });
  };

  static crossDeviceDocumentGet = id => {
    return API.post('crossdevicedocument/get', {
      id
    });
  };

  static crossDeviceDocumentComplete = (id, pages, documentOptions) => {
    return API.post('crossdevicedocument/complete', {
      id,
      pages,
      documentOptions
    });
  };

  static validateWithExternalAPI = (
    externalApiId,
    recipientRequestId,
    data,
    templateMode
  ) => {
    return API.post('external-api/validate', {
      externalApiId,
      ...(templateMode
        ? { templateId: recipientRequestId }
        : { recipientRequestId }),
      data
    });
  };
}
